import React, { useState, useEffect } from 'react';
import './AdminOrders.css';
import {
  Pane,
  Table,
  Heading,
  Text,
  SelectMenu,
  Button,
  Spinner,
  Alert,
  Dialog,
  majorScale
} from 'evergreen-ui';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newDeliveryStatus, setNewDeliveryStatus] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const ordersResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/orders/all-orders`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!ordersResponse.ok) {
          const errorData = await ordersResponse.json();
          throw new Error(errorData.message || 'Failed to fetch orders');
        }

        const ordersData = await ordersResponse.json();
        setOrders(ordersData);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to fetch orders');
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleUpdateDeliveryStatus = async () => {
    if (!selectedOrder) return;
    try {
      setUpdateLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/orders/${selectedOrder.id}/delivery-status`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ deliveryStatus: newDeliveryStatus }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update delivery status');
      }

      const updatedOrder = await response.json();
      setOrders(
        orders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, deliveryStatus: updatedOrder.order.deliveryStatus }
            : order
        )
      );
      setIsDialogOpen(false);
      setSelectedOrder(null);
      setNewDeliveryStatus('');
      setUpdateLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to update delivery status');
      setUpdateLoading(false);
    }
  };

  const statusOptions = [
    'order_received',
    'processing',
    'shipped',
    'delivered',
    'ready_for_pickup',
  ].map((status) => ({
    label: status.replace('_', ' ').toUpperCase(),
    value: status,
  }));

  const getStatusColor = (status) => {
    switch (status) {
      case 'order_received':
        return '#FFE5B4'; // Light orange for new orders
      case 'processing':
        return '#ADD8E6'; // Light blue for processing
      case 'shipped':
        return '#FFDAB9'; // Peach for shipped
      case 'delivered':
        return '#98FB98'; // Light green for delivered
      case 'ready_for_pickup':
        return '#E6E6FA'; // Lavender for ready for pickup
      default:
        return '#FFFFFF'; // White for unknown status
    }
  };

  // Helper function to get item names from OrderItems
  const getItemNames = (orderItems) => {
    if (!orderItems || orderItems.length === 0) return 'No items';
    return orderItems
      .map((item) => item.Product?.product_title || 'Unknown Product')
      .join(', ');
  };

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane padding={majorScale(3)}>
        <Alert intent="danger" title="Error">
          {error}
          <Button
            marginLeft={majorScale(2)}
            onClick={() => {
              setError(null);
              setLoading(true);
              window.location.reload();
            }}
          >
            Retry
          </Button>
        </Alert>
      </Pane>
    );
  }

  return (
    <Pane
      maxWidth={1200}
      margin="auto"
      padding={majorScale(3)}
      className="admin-orders-container"
    >
      <Heading size={800} marginBottom={majorScale(3)}>
        Manage Orders
      </Heading>
      {orders.length === 0 ? (
        <Text>No orders found.</Text>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Status</Table.TextHeaderCell>
            <Table.TextHeaderCell>Order ID</Table.TextHeaderCell>
            <Table.TextHeaderCell>Customer</Table.TextHeaderCell>
            <Table.TextHeaderCell>Phone</Table.TextHeaderCell>
            <Table.TextHeaderCell>Items</Table.TextHeaderCell>
            <Table.TextHeaderCell>Total (USD)</Table.TextHeaderCell>
            <Table.TextHeaderCell>Payment Status</Table.TextHeaderCell>
            <Table.TextHeaderCell>Pickup Point</Table.TextHeaderCell>
            <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {orders.map((order) => (
              <Table.Row key={order.id}>
                <Table.TextCell
                  style={{
                    backgroundColor: getStatusColor(order.deliveryStatus),
                    fontWeight: 'bold',
                  }}
                >
                  {order.deliveryStatus
                    ? order.deliveryStatus.replace('_', ' ').toUpperCase()
                    : 'N/A'}
                </Table.TextCell>
                <Table.TextCell>#{order.id}</Table.TextCell>
                <Table.TextCell>{order.customerName || 'N/A'}</Table.TextCell>
                <Table.TextCell>{order.customerPhone || 'N/A'}</Table.TextCell>
                <Table.TextCell>{getItemNames(order.OrderItems)}</Table.TextCell>
                <Table.TextCell>
                  {order.totalAmount ? order.totalAmount.toFixed(2) : '0.00'}
                </Table.TextCell>
                <Table.TextCell>
                  {order.CheckoutPesapalTransaction?.status?.toUpperCase() ||
                    order.status?.toUpperCase() ||
                    'N/A'}
                </Table.TextCell>
                <Table.TextCell>{order.Location?.pickupPoint || 'N/A'}</Table.TextCell>
                <Table.TextCell>
                  <Button
                    appearance="primary"
                    onClick={() => {
                      setSelectedOrder(order);
                      setNewDeliveryStatus(order.deliveryStatus);
                      setIsDialogOpen(true);
                    }}
                  >
                    Edit Status
                  </Button>
                </Table.TextCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Dialog for Editing Delivery Status */}
      <Dialog
        isShown={isDialogOpen}
        title="Update Delivery Status"
        onCloseComplete={() => {
          setIsDialogOpen(false);
          setSelectedOrder(null);
          setNewDeliveryStatus('');
        }}
        confirmLabel="Save"
        onConfirm={handleUpdateDeliveryStatus}
        isConfirmDisabled={!newDeliveryStatus || updateLoading}
        isConfirmLoading={updateLoading}
      >
        <Pane>
          <Text marginBottom={majorScale(2)} display="block">
            Order #{selectedOrder?.id}
          </Text>
          <SelectMenu
            title="Select Status"
            options={statusOptions}
            selected={newDeliveryStatus}
            onSelect={(item) => setNewDeliveryStatus(item.value)}
          >
            <Button>
              {newDeliveryStatus
                ? newDeliveryStatus.replace('_', ' ').toUpperCase()
                : 'Select Status'}
            </Button>
          </SelectMenu>
        </Pane>
      </Dialog>
    </Pane>
  );
};

export default AdminOrders;